import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  ApiErrorBody,
  PaginatedResult,
  PanelCreateContractInput,
  PanelCreditContractDto,
  PanelCreditLedgerEntryDto,
  PanelCreditOverviewDto,
  PanelCreditReversalDto,
  PanelGrantCreditsInput,
  PanelGrantCreditsResultDto,
  PanelReverseCreditsInput,
  PanelTerminateContractInput,
} from '@icebreaker/shared';
import { createAsyncActions } from '@icebreaker/shared/store';

/**
 * KURUM KREDİSİ DEPOSU (kurum satışla açılır · Story D5).
 *
 * Özet ve defter AYRI dallardır: kurum detayı bölümleri bağımsız yükler, biri
 * hata verirse diğeri çalışır (tasarım P5). Her yazma kendi mutasyon durumunu
 * taşır; diyalog kapanınca sıfırlanır. Yazma başarılı olunca özet, defter ve
 * kurum listesi sunucudan tazelenir (`INVALIDATION_MAP` `panelCredits/*`).
 */
export const fetchCreditOverview = createAsyncActions<
  { organizationId: string },
  PanelCreditOverviewDto
>('panelCredits/overviewFetch');
export const fetchCreditLedger = createAsyncActions<
  { organizationId: string; page: number; pageSize: number },
  PaginatedResult<PanelCreditLedgerEntryDto>
>('panelCredits/ledgerFetch');
export const grantCredits = createAsyncActions<
  { organizationId: string; body: PanelGrantCreditsInput },
  { organizationId: string; grant: PanelGrantCreditsResultDto }
>('panelCredits/grant');
export const reverseCredits = createAsyncActions<
  { organizationId: string; grantId: string; body: PanelReverseCreditsInput },
  { organizationId: string; reversal: PanelCreditReversalDto }
>('panelCredits/reverse');
export const createContract = createAsyncActions<
  { organizationId: string; body: PanelCreateContractInput },
  { organizationId: string; contract: PanelCreditContractDto }
>('panelCredits/contractCreate');
export const terminateContract = createAsyncActions<
  { organizationId: string; contractId: string; body: PanelTerminateContractInput },
  { organizationId: string; contract: PanelCreditContractDto }
>('panelCredits/contractTerminate');

type Phase = 'idle' | 'loading' | 'succeeded' | 'failed';

export type CreditMutationKey = 'grant' | 'reverse' | 'contractCreate' | 'contractTerminate';

interface MutationState {
  phase: Phase;
  error: ApiErrorBody | null;
}

/** Başarı sonrası defterde vurgulanacak satır (tasarım P6 "yeni satır vurgulanır"). */
export type LedgerHighlight =
  | { readonly kind: 'entry'; readonly id: string }
  | { readonly kind: 'grant'; readonly id: string };

export interface CreditsState {
  overview: {
    organizationId: string | null;
    data: PanelCreditOverviewDto | null;
    phase: Phase;
    error: ApiErrorBody | null;
  };
  ledger: {
    organizationId: string | null;
    items: PanelCreditLedgerEntryDto[];
    total: number;
    page: number;
    pageSize: number;
    hasNext: boolean;
    phase: Phase;
    error: ApiErrorBody | null;
  };
  mutations: Record<CreditMutationKey, MutationState>;
  highlight: LedgerHighlight | null;
  /**
   * Son başarılı yüklemenin yanıtı: "Kredi yükle" penceresi başarı özetini
   * (ödenen hak, hediye, kod kullanımı) bundan çizer (P6r "Başarılı").
   */
  grantResult: PanelGrantCreditsResultDto | null;
}

const idle: MutationState = { phase: 'idle', error: null };

const initialState: CreditsState = {
  overview: { organizationId: null, data: null, phase: 'idle', error: null },
  ledger: {
    organizationId: null,
    items: [],
    total: 0,
    page: 1,
    pageSize: 0,
    hasNext: false,
    phase: 'idle',
    error: null,
  },
  mutations: { grant: idle, reverse: idle, contractCreate: idle, contractTerminate: idle },
  highlight: null,
  grantResult: null,
};

const slice = createSlice({
  name: 'panelCredits',
  initialState,
  reducers: {
    mutationReset(state, action: PayloadAction<CreditMutationKey>) {
      state.mutations[action.payload] = idle;
      if (action.payload === 'grant') state.grantResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCreditOverview.request, (state, action) => {
        if (state.overview.organizationId !== action.payload.organizationId) {
          state.overview.data = null;
          state.highlight = null;
        }
        state.overview.organizationId = action.payload.organizationId;
        state.overview.phase = 'loading';
        state.overview.error = null;
      })
      .addCase(fetchCreditOverview.success, (state, action) => {
        state.overview.phase = 'succeeded';
        state.overview.data = cloneOverview(action.payload);
      })
      .addCase(fetchCreditOverview.failure, (state, action) => {
        state.overview.phase = 'failed';
        state.overview.error = action.payload;
      })
      .addCase(fetchCreditLedger.request, (state, action) => {
        if (state.ledger.organizationId !== action.payload.organizationId) state.ledger.items = [];
        state.ledger.organizationId = action.payload.organizationId;
        state.ledger.page = action.payload.page;
        state.ledger.phase = 'loading';
        state.ledger.error = null;
      })
      .addCase(fetchCreditLedger.success, (state, action) => {
        state.ledger.phase = 'succeeded';
        state.ledger.items = action.payload.items.map((item) => ({ ...item }));
        state.ledger.total = action.payload.total;
        state.ledger.page = action.payload.page;
        state.ledger.pageSize = action.payload.pageSize;
        state.ledger.hasNext = action.payload.hasNext;
      })
      .addCase(fetchCreditLedger.failure, (state, action) => {
        state.ledger.phase = 'failed';
        state.ledger.error = action.payload;
      });

    /*
     * Dört yazmanın mutasyon durumu. `addCase` aynı eylem için iki kez
     * çağrılamaz: başarıdaki vurgu da burada, aynı işleyicide yazılır.
     */
    builder
      .addCase(grantCredits.request, (state) => {
        state.mutations.grant = { phase: 'loading', error: null };
        state.grantResult = null;
      })
      .addCase(grantCredits.success, (state, action) => {
        state.mutations.grant = { phase: 'succeeded', error: null };
        state.highlight = { kind: 'grant', id: action.payload.grant.id };
        state.grantResult = cloneGrantResult(action.payload.grant);
      })
      .addCase(grantCredits.failure, (state, action) => {
        state.mutations.grant = { phase: 'failed', error: action.payload };
      })
      .addCase(reverseCredits.request, (state) => {
        state.mutations.reverse = { phase: 'loading', error: null };
      })
      .addCase(reverseCredits.success, (state, action) => {
        state.mutations.reverse = { phase: 'succeeded', error: null };
        state.highlight = { kind: 'entry', id: action.payload.reversal.entryId };
      })
      .addCase(reverseCredits.failure, (state, action) => {
        state.mutations.reverse = { phase: 'failed', error: action.payload };
      })
      .addCase(createContract.request, (state) => {
        state.mutations.contractCreate = { phase: 'loading', error: null };
      })
      .addCase(createContract.success, (state) => {
        state.mutations.contractCreate = { phase: 'succeeded', error: null };
      })
      .addCase(createContract.failure, (state, action) => {
        state.mutations.contractCreate = { phase: 'failed', error: action.payload };
      })
      .addCase(terminateContract.request, (state) => {
        state.mutations.contractTerminate = { phase: 'loading', error: null };
      })
      .addCase(terminateContract.success, (state) => {
        state.mutations.contractTerminate = { phase: 'succeeded', error: null };
      })
      .addCase(terminateContract.failure, (state, action) => {
        state.mutations.contractTerminate = { phase: 'failed', error: action.payload };
      });
  },
});

function cloneGrantResult(result: PanelGrantCreditsResultDto) {
  return {
    ...result,
    grantedBy: { ...result.grantedBy },
    gift: result.gift
      ? {
          ...result.gift,
          grant: { ...result.gift.grant, grantedBy: { ...result.gift.grant.grantedBy } },
        }
      : null,
  };
}

/** Salt okunur DTO dizilerini depoya yazılabilir kopyayla koyar. */
function cloneOverview(overview: PanelCreditOverviewDto) {
  const { summary } = overview;
  return {
    summary: {
      ...summary,
      byKind: { ...summary.byKind },
      expiring: summary.expiring.map((batch) => ({ ...batch })),
      activeContract: summary.activeContract ? { ...summary.activeContract } : null,
      nextPublish: summary.nextPublish ? { ...summary.nextPublish } : null,
    },
    grants: overview.grants.map((grant) => ({ ...grant, grantedBy: { ...grant.grantedBy } })),
    contracts: overview.contracts.map((contract) => ({
      ...contract,
      createdBy: { ...contract.createdBy },
      terminatedBy: contract.terminatedBy ? { ...contract.terminatedBy } : null,
    })),
  };
}

export const { mutationReset: creditMutationReset } = slice.actions;
export const creditsReducer = slice.reducer;
